// ============================================================================
// ✨ PANTALLA DE BIENVENIDA — se muestra tras el login mientras carga el ERP.
// ============================================================================
// Animación "Strands" portada a WebGL2 puro (sin React ni ogl, que habrían
// añadido cientos de KB a un bundle IIFE). Se dibuja en DOS capas —unas hebras
// detrás del logo y otras delante— de modo que la marca queda tejida entre
// ellas; la capa frontal usa mix-blend-mode:screen para SUMAR luz sobre el
// logo en vez de taparlo.
//
// La barra de progreso refleja cargas REALES (ver arrancarDespuesDeLogin), no
// un retraso artificial. Como el arranque optimizado tarda ~1s y la animación
// merece verse, session.js mantiene la pantalla un mínimo en pantalla.
//
// Si el equipo no soporta WebGL2, la pantalla se muestra igual (fondo navy y
// marca) sin animación: nunca bloquea el ingreso.
import { AURA_ISOTIPO, BRAND } from "../core/config.js";

const MAX_STRANDS = 12;
const MAX_COLORS = 8;

// Paleta AURA: cian de marca → navy.
const COLORS = ["#22ECFF", "#00B8D9", "#2B4A82", "#4C5FA8"];

// Valores de fábrica, afinados sobre el borrador. Un administrador puede
// ajustarlos desde Configuración → Pantalla de bienvenida; lo que guarde vive
// en erp:splash_config y se sincroniza a la nube como cualquier configuración.
export const SPLASH_DEFAULTS = {
  count: 6,        // hebras totales
  detras: 3,       // cuántas van detrás del logo
  brilloFrente: 0.5,
  speed: 0.1,
  amplitude: 1.6,
  waviness: 1,
  thickness: 0.6,
  glow: 2.8,
  taper: 2.6,
  spread: 1.2,
  intensity: 0.55,
  saturation: 1.2,
  scale: 1.85,
  offset: -0.01,   // ajuste fino de la altura de la banda
  segundos: 6      // cuánto permanece en pantalla como mínimo
};

export const SPLASH_KEY = "erp:splash_config";

// Caché en memoria: el bucle de dibujo lee la configuración en CADA cuadro para
// que los ajustes del panel se vean en vivo, así que no puede estar parseando
// JSON 60 veces por segundo. saveSplashConfig refresca esta caché.
let _cfg = null;

export function getSplashConfig() {
  if (_cfg) return _cfg;
  let guardado = {};
  try {
    const raw = localStorage.getItem(SPLASH_KEY);
    if (raw) guardado = JSON.parse(raw) || {};
  } catch { guardado = {}; }
  _cfg = { ...SPLASH_DEFAULTS, ...guardado };
  return _cfg;
}

export function saveSplashConfig(cfg) {
  _cfg = { ...SPLASH_DEFAULTS, ...cfg };
  try { localStorage.setItem(SPLASH_KEY, JSON.stringify(_cfg)); } catch {}
  return _cfg;
}

// La configuración pudo cambiar en otra sesión (Realtime) o al recargar caché.
export function refrescarSplashConfig() { _cfg = null; }

const VERT = `#version 300 es
in vec2 position;
void main(){ gl_Position = vec4(position, 0.0, 1.0); }`;

const FRAG = `#version 300 es
precision highp float;
uniform float uTime;
uniform vec2  uResolution;
uniform vec3  uColors[${MAX_COLORS}];
uniform int   uColorCount, uStrandCount, uFrom, uTo;
uniform float uCenterY, uSpeed, uAmplitude, uWaviness, uThickness, uGlow,
              uTaper, uSpread, uIntensity, uOpacity, uScale, uSaturation;
out vec4 fragColor;
const float PI = 3.14159265;

vec3 samplePalette(float t){
  t = fract(t);
  float scaled = t * float(uColorCount);
  int idx = int(floor(scaled));
  float blend = fract(scaled);
  int nextIdx = idx + 1;
  if (nextIdx >= uColorCount) nextIdx = 0;
  return mix(uColors[idx], uColors[nextIdx], blend);
}

void main(){
  vec2 uv = (gl_FragCoord.xy - 0.5*uResolution) / uResolution.y;
  uv.y -= uCenterY;                       // ancla la banda al bloque de marca
  uv /= max(uScale, 0.0001);

  float e = 0.06 + uIntensity*0.94;
  float env = pow(max(cos(uv.x*PI*1.3), 0.0), uTaper);
  vec3 col = vec3(0.0);

  for (int i = 0; i < ${MAX_STRANDS}; i++){
    if (i >= uStrandCount) break;
    if (i < uFrom || i >= uTo) continue;   // esta hebra pertenece a la otra capa

    float fi = float(i);
    float ph = fi*1.7*uSpread;
    float freq = (2.0 + fi*0.35)*uWaviness;
    float spd = 1.4 + fi*1.2;
    float tt = uTime*uSpeed;
    float w = sin(uv.x*freq + tt*spd + ph)*0.60
            + sin(uv.x*freq*1.1 - tt*spd*0.7 + ph*1.7)*0.40;
    float amp = (0.1 + 0.02*e)*env*uAmplitude;
    float y = w*amp;
    float d = abs(uv.y - y);
    float thick = (0.001 + 0.05*e)*(0.35 + env)*uThickness;
    float g = thick/(d + thick*0.45);
    g = g*g;
    // el matiz se reparte sobre el TOTAL: así ambas capas comparten familia
    // cromática y se leen como una sola animación tejida, no como dos pegadas.
    float h = fi/float(uStrandCount) + uv.x*0.30 + uTime*0.04;
    col += samplePalette(h)*g*env;
  }

  col *= 0.45 + 0.7*e;
  col = 1.0 - exp(-col*uGlow);
  float gray = dot(col, vec3(0.2126,0.7152,0.0722));
  col = max(mix(vec3(gray), col, uSaturation), 0.0);
  float lum = max(max(col.r, col.g), col.b);
  fragColor = vec4(col*uOpacity, clamp(lum,0.0,1.0)*uOpacity);
}`;

// Una capa = un lienzo con su propio contexto WebGL2.
function crearCapa(canvas) {
  let gl;
  try { gl = canvas.getContext("webgl2", { alpha: true, premultipliedAlpha: true, antialias: true }); }
  catch { return null; }
  if (!gl) return null;

  function compilar(tipo, src) {
    const s = gl.createShader(tipo);
    gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
    return s;
  }

  let prog;
  try {
    prog = gl.createProgram();
    gl.attachShader(prog, compilar(gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compilar(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog));
  } catch (e) {
    console.warn("Bienvenida sin animación (shader):", e.message);
    return null;
  }
  gl.useProgram(prog);

  gl.bindVertexArray(gl.createVertexArray());
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(prog, "position");
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

  gl.clearColor(0, 0, 0, 0);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

  const U = n => gl.getUniformLocation(prog, n);
  const u = {
    time: U("uTime"), res: U("uResolution"), colors: U("uColors"), colorCount: U("uColorCount"),
    count: U("uStrandCount"), from: U("uFrom"), to: U("uTo"), centerY: U("uCenterY"),
    speed: U("uSpeed"), amp: U("uAmplitude"), wav: U("uWaviness"), thick: U("uThickness"),
    glow: U("uGlow"), taper: U("uTaper"), spread: U("uSpread"), int: U("uIntensity"),
    op: U("uOpacity"), scale: U("uScale"), sat: U("uSaturation")
  };

  const pal = new Float32Array(MAX_COLORS * 3);
  for (let i = 0; i < MAX_COLORS; i++) {
    const hex = COLORS[i] || COLORS[COLORS.length - 1];
    const n = parseInt(hex.slice(1), 16);
    pal[i * 3] = ((n >> 16) & 255) / 255;
    pal[i * 3 + 1] = ((n >> 8) & 255) / 255;
    pal[i * 3 + 2] = (n & 255) / 255;
  }
  gl.uniform3fv(u.colors, pal);
  gl.uniform1i(u.colorCount, COLORS.length);

  function resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.max(1, Math.round(canvas.clientWidth * dpr));
    canvas.height = Math.max(1, Math.round(canvas.clientHeight * dpr));
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.useProgram(prog);
    gl.uniform2f(u.res, canvas.width, canvas.height);
  }
  resize();

  return {
    resize,
    // Los parámetros se envían en CADA cuadro (no una sola vez al crear la capa)
    // para que los ajustes del panel de administración se vean en vivo.
    dibujar(t, p, centerY, from, to, opacidad) {
      gl.useProgram(prog);
      gl.uniform1f(u.time, t);
      gl.uniform1f(u.centerY, centerY);
      gl.uniform1i(u.count, p.count);
      gl.uniform1i(u.from, from);
      gl.uniform1i(u.to, to);
      gl.uniform1f(u.op, opacidad);
      gl.uniform1f(u.speed, p.speed);
      gl.uniform1f(u.amp, p.amplitude);
      gl.uniform1f(u.wav, p.waviness);
      gl.uniform1f(u.thick, p.thickness);
      gl.uniform1f(u.glow, p.glow);
      gl.uniform1f(u.taper, p.taper);
      gl.uniform1f(u.spread, p.spread);
      gl.uniform1f(u.int, p.intensity);
      gl.uniform1f(u.sat, p.saturation);
      gl.uniform1f(u.scale, p.scale);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    },
    destruir() {
      try { gl.getExtension("WEBGL_lose_context")?.loseContext(); } catch {}
    }
  };
}

const CSS = `
/* Entrada en dos tiempos, para que el login no desaparezca de golpe:
   1) invisible → visible con el fondo TRASLÚCIDO y desenfoque de fondo, así se
      ve el formulario difuminarse detrás mientras entran las hebras;
   2) .__solido → el fondo pasa a opaco, ocultando el ERP que se construye debajo. */
/* La pantalla se construye OCULTA mientras el login espera a la red, y solo se
   revela cuando todo está listo. Así el fundido de entrada es una transición de
   opacidad pura sobre una capa ya compuesta —igual que la de salida, que siempre
   se vio suave— y no compite con la compilación de los shaders.
   will-change:opacity la promueve a su propia capa: el fundido corre en el
   compositor y no lo frena el trabajo del hilo principal. */
#__aura_splash{position:fixed;inset:0;z-index:99998;display:flex;align-items:center;justify-content:center;
  background:radial-gradient(ellipse at 50% 45%,rgba(18,38,77,.86) 0%,rgba(11,19,43,.92) 70%);
  -webkit-backdrop-filter:blur(16px) saturate(.85);backdrop-filter:blur(16px) saturate(.85);
  overflow:hidden;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
  opacity:0;will-change:opacity;pointer-events:none;
  transition:opacity 1s cubic-bezier(.33,0,.2,1),visibility 1s,background 1.1s ease}
#__aura_splash.__in{opacity:1;pointer-events:auto}
/* Red de seguridad contra el destello claro: mientras la bienvenida está en
   pantalla, el fondo de la página deja de ser el gris casi blanco del ERP
   (#f8fafc). Si por cualquier motivo queda un instante sin capa que cubra —el
   hilo principal bloqueado justo cuando se reconstruye #root, por ejemplo— lo
   que se ve es el mismo navy de la bienvenida, no un fogonazo blanco. */
html.__aura_fondo, html.__aura_fondo body{background:#0B132B}
#__aura_splash.__solido{background:radial-gradient(ellipse at 50% 45%,#12264D 0%,#0B132B 70%)}
#__aura_splash.__out{opacity:0;visibility:hidden;pointer-events:none}
/* Las hebras entran con su propio fundido, un poco por detrás del de la capa:
   así no aparecen de golpe aunque los primeros cuadros del shader sean pesados. */
#__aura_splash .__cv{opacity:0;transition:opacity 1.2s ease .25s}
#__aura_splash.__in .__cv{opacity:1}
/* El contenido (marca, saludo, barra) solo se anima al revelarse, no al crearse
   oculto: si no, las animaciones de entrada se consumirían durante la espera. */
#__aura_splash .__ct{opacity:0;transform:translateY(14px);
  transition:opacity .85s cubic-bezier(.16,.8,.3,1) .15s,transform .85s cubic-bezier(.16,.8,.3,1) .15s}
#__aura_splash.__in .__ct{opacity:1;transform:none}
#__aura_splash .__cerrar{position:absolute;top:16px;right:18px;z-index:5;border:1px solid rgba(0,229,255,.35);
  background:rgba(0,229,255,.10);color:#22ECFF;font:700 12px/1 inherit;padding:9px 16px;border-radius:8px;
  cursor:pointer;font-family:inherit}
#__aura_splash .__cerrar:hover{background:rgba(0,229,255,.2)}
#__aura_splash .__cv{position:absolute;inset:0;width:100%;height:100%;display:block}
#__aura_splash .__back{z-index:1}
#__aura_splash .__front{z-index:3;pointer-events:none;mix-blend-mode:screen}
#__aura_splash .__ct{position:relative;z-index:2;text-align:center;color:#fff;padding:20px}
#__aura_splash.__in .__iso{width:80px;height:80px;margin:0 auto 20px;display:block;
  filter:drop-shadow(0 6px 30px rgba(0,229,255,.45));animation:__auraUp .8s cubic-bezier(.16,.8,.3,1) both}
#__aura_splash.__in .__word{font-size:62px;font-weight:800;letter-spacing:12px;line-height:1;margin-bottom:10px;color:#fff;
  text-shadow:0 0 34px rgba(11,19,43,.9),0 0 14px rgba(11,19,43,.8);
  animation:__auraUp .8s .08s cubic-bezier(.16,.8,.3,1) both}
#__aura_splash.__in .__sub{font-size:12px;font-weight:700;letter-spacing:8px;color:#22ECFF;margin-bottom:30px;
  text-shadow:0 0 18px rgba(11,19,43,.9);animation:__auraUp .8s .16s cubic-bezier(.16,.8,.3,1) both}
#__aura_splash.__in .__hi{font-size:15.5px;color:#dbe6f5;margin-bottom:5px;text-shadow:0 0 14px rgba(11,19,43,.85);
  animation:__auraUp .8s .26s cubic-bezier(.16,.8,.3,1) both}
#__aura_splash .__hi b{color:#fff;font-weight:700}
#__aura_splash.__in .__rol{font-size:12px;color:#8ba1c2;margin-bottom:28px;
  animation:__auraUp .8s .3s cubic-bezier(.16,.8,.3,1) both}
#__aura_splash.__in .__bar{width:230px;height:3px;margin:0 auto;background:rgba(255,255,255,.12);border-radius:99px;
  overflow:hidden;animation:__auraUp .8s .36s cubic-bezier(.16,.8,.3,1) both}
#__aura_splash .__bar i{display:block;height:100%;width:0;border-radius:99px;
  background:linear-gradient(90deg,#22ECFF,#00B8D9);transition:width .45s ease}
#__aura_splash.__in .__step{font-size:11px;color:#7f95b8;margin-top:11px;letter-spacing:.4px;min-height:14px;
  animation:__auraUp .8s .4s cubic-bezier(.16,.8,.3,1) both}
@keyframes __auraUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}
@media (prefers-reduced-motion:reduce){
  #__aura_splash .__iso,#__aura_splash .__word,#__aura_splash .__sub,
  #__aura_splash .__hi,#__aura_splash .__rol,#__aura_splash .__bar,#__aura_splash.__in .__step{animation:none}
}
`;

// Abre la pantalla y devuelve el control para actualizar el progreso y cerrarla.
// demo:true la abre sin barra de progreso y con botón de cierre, para que un
// administrador pruebe sus ajustes desde Configuración.
export function abrirSplash({ nombre, rolLabel, rolIcon, empresa, demo, oculto } = {}) {
  // Si ya hubiera una abierta (re-login sin recargar), se retira primero.
  document.getElementById("__aura_splash")?.remove();

  const esc = s => String(s == null ? "" : s)
    .replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

  if (!document.getElementById("__aura_splash_css")) {
    const st = document.createElement("style");
    st.id = "__aura_splash_css";
    st.textContent = CSS;
    document.head.appendChild(st);
  }

  const el = document.createElement("div");
  el.id = "__aura_splash";
  el.innerHTML = `
    <canvas class="__cv __back"></canvas>
    <div class="__ct">
      <div class="__brand">
        <img class="__iso" src="${AURA_ISOTIPO}" alt=""/>
        <div class="__word">AURA</div>
        <div class="__sub">ENTERPRISE ERP</div>
      </div>
      ${nombre ? `<div class="__hi">Bienvenido de vuelta, <b>${esc(nombre)}</b></div>` : `<div class="__hi">Bienvenido a <b>${esc(BRAND.producto)}</b></div>`}
      <div class="__rol">${rolIcon ? esc(rolIcon) + " " : ""}${esc(rolLabel || "")}${empresa ? " · " + esc(empresa) : ""}</div>
      ${demo ? "" : `<div class="__bar"><i></i></div><div class="__step">Conectando…</div>`}
    </div>
    <canvas class="__cv __front"></canvas>
    ${demo ? `<button class="__cerrar" type="button">Cerrar vista previa</button>` : ""}
  `;
  document.body.appendChild(el);

  // Tras el fundido, el fondo pasa a opaco: a partir de ahí render() reconstruye
  // #root con el ERP y no debe verse asomar. Es idempotente porque lo dispara lo
  // que ocurra primero: este temporizador o la llamada explícita desde
  // session.js justo antes de render(). Dejarlo solo al temporizador sería una
  // carrera contra la red: si la carga terminara antes, el ERP se construiría
  // detrás de un fondo todavía translúcido.
  const solidificar = () => el.classList.add("__solido");
  let tOpaco = 0;

  // Revelar = añadir .__in. Todo lo pesado (contextos WebGL, compilación de
  // shaders) ya ocurrió al construir la capa, así que aquí solo queda una
  // transición de opacidad sobre una capa ya compuesta — exactamente igual que
  // la de salida, que siempre se vio suave.
  //
  // El doble requestAnimationFrame no es un adorno: con uno solo el navegador
  // puede calcular el estado inicial (opacity:0) y el final en el mismo lote de
  // estilo, y entonces NO hay transición: la pantalla aparece de golpe. Se
  // fuerza además una lectura de layout para que el estado inicial quede firme.
  // Devuelve una PROMESA que se resuelve cuando la capa cubre de verdad (opacidad
  // 1 y fondo opaco). Quien vaya a destapar lo que hay debajo —render() borra el
  // login de #root— debe esperarla: si no, queda un instante con el login ya
  // retirado y el splash aún transparente, y se ve el fondo claro del body.
  let revelado = false;
  let cubierta = null;
  function revelar(datos) {
    if (revelado) return cubierta;
    revelado = true;
    if (datos) actualizarTextos(datos);
    void el.offsetHeight;                       // fija el estado inicial
    cubierta = new Promise(resolve => {
      let listo = false;
      const terminar = () => {
        if (listo) return;
        listo = true;
        clearTimeout(tOpaco);
        solidificar();                          // fondo opaco: ya no se ve nada debajo
        resolve();
      };
      // El fin de la transición de opacidad de la PROPIA capa (los hijos también
      // animan opacidad y el evento burbujea, de ahí la comprobación del target).
      el.addEventListener("transitionend", e => {
        if (e.target === el && e.propertyName === "opacity") terminar();
      });
      // Red de seguridad: con la pestaña en segundo plano o con "reducir
      // movimiento", transitionend puede no llegar nunca y el login se quedaría
      // esperando para siempre.
      tOpaco = setTimeout(terminar, 1400);
      requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add("__in")));
    });
    return cubierta;
  }

  const barra = el.querySelector(".__bar i");
  const paso = el.querySelector(".__step");
  const marca = el.querySelector(".__brand");
  const elHi = el.querySelector(".__hi");
  const elRol = el.querySelector(".__rol");

  // Al precalentar aún no se conoce el nombre ni el rol (llegan con el perfil):
  // se rellenan al revelar.
  function actualizarTextos({ nombre, rolLabel, rolIcon, empresa } = {}) {
    if (elHi) {
      elHi.innerHTML = nombre
        ? `Bienvenido de vuelta, <b>${esc(nombre)}</b>`
        : `Bienvenido a <b>${esc(BRAND.producto)}</b>`;
    }
    if (elRol) {
      elRol.textContent = `${rolIcon ? esc(rolIcon) + " " : ""}${rolLabel || ""}${empresa ? " · " + empresa : ""}`;
    }
  }
  const cvBack = el.querySelector(".__back");
  const cvFront = el.querySelector(".__front");

  const back = crearCapa(cvBack);
  const front = crearCapa(cvFront);
  let raf = 0;
  const onResize = () => { back?.resize(); front?.resize(); };

  if (back && front) {
    window.addEventListener("resize", onResize);
    // La banda de hebras se alinea al bloque de marca midiendo su posición real,
    // así queda centrada en el logo en cualquier pantalla (no un valor fijo).
    const centroBanda = p => {
      const cr = cvBack.getBoundingClientRect();
      const br = marca.getBoundingClientRect();
      if (!cr.height) return p.offset;
      return (0.5 - ((br.top + br.height / 2) - cr.top) / cr.height) + p.offset;
    };
    const frame = ms => {
      const p = getSplashConfig();          // se relee en vivo: el panel se ve al instante
      const t = ms * 0.001;
      const c = centroBanda(p);
      const detras = Math.min(p.detras, p.count);
      back.dibujar(t, p, c, 0, detras, 1);                     // hebras de atrás
      front.dibujar(t, p, c, detras, p.count, p.brilloFrente); // hebras de adelante
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
  }

  const control = {
    // Muestra la pantalla ya construida (y rellena el saludo si llega ahora).
    revelar,
    // Cierra el fondo translúcido antes de reconstruir el ERP debajo.
    solidificar() { clearTimeout(tOpaco); solidificar(); },
    // Actualiza barra y texto. Se llama desde las cargas reales.
    progreso(pct, texto) {
      if (barra) barra.style.width = Math.max(0, Math.min(100, pct)) + "%";
      if (texto && paso) paso.textContent = texto;
    },
    cerrar() {
      if (raf) cancelAnimationFrame(raf);
      clearTimeout(tOpaco);
      window.removeEventListener("resize", onResize);
      // El ERP ya está construido debajo y pinta su propio fondo: se libera el
      // navy de emergencia para que el fundido de salida lo descubra tal cual.
      document.documentElement.classList.remove("__aura_fondo");
      el.classList.add("__out");
      setTimeout(() => {
        back?.destruir();
        front?.destruir();
        el.remove();
      }, 700);
    }
  };

  document.documentElement.classList.add("__aura_fondo");

  el.querySelector(".__cerrar")?.addEventListener("click", () => control.cerrar());
  // oculto:true la deja construida y en silencio, lista para revelarse cuando el
  // login termine de verificar. Sin esa opción se revela de inmediato.
  if (!oculto) revelar();
  return control;
}
